/**
 * Centralized Analytics Module (P20250925-098, updated P20250925-102)
 *
 * Privacy-safe event tracking with PostHog, strict env gating,
 * and automatic PII sanitization via Data Governance module.
 *
 * CRITICAL: No emails, names, phone numbers in event properties.
 * All props pass through sanitizeProps before transmission.
 */

import { initPostHog, capture as posthogCapture, isPostHogReady } from '../posthog';
import { v4 as uuidv4 } from 'uuid';
import { sanitizeProps } from '../governance/sanitize';
import { getConsent, onConsentChange } from '../privacy/consent';

// Environment flags (P20250925-147: Added FEATURE_ANALYTICS_ENABLED gate)
export const ANALYTICS_FLAGS = {
  FEATURE_ANALYTICS_ENABLED: import.meta.env.VITE_FEATURE_ANALYTICS_ENABLED !== 'false', // Master kill switch
  ANALYTICS_ENABLED: import.meta.env.VITE_ANALYTICS_ENABLED !== 'false', // Default true
  POSTHOG_ENABLED: !!import.meta.env.VITE_POSTHOG_KEY,
  CLARITY_ENABLED: import.meta.env.VITE_CLARITY_ENABLED === 'true' && import.meta.env.PROD,
  SALESIQ_ENABLED: import.meta.env.VITE_SALESIQ_ENABLED === 'true' && import.meta.env.PROD,
  EXPERIMENTS_ENABLED: import.meta.env.VITE_EXPERIMENTS_ENABLED === 'true',
};

// Global state
let sessionId: string | null = null;
let analyticsInitialized = false;

/**
 * Get or create session ID
 */
export function getSessionId(): string {
  if (!sessionId) {
    const key = 'ns.session';
    sessionId = localStorage.getItem(key);

    if (!sessionId) {
      sessionId = uuidv4();
      localStorage.setItem(key, sessionId);
    }
  }

  return sessionId;
}

// Removed: stripPII replaced by sanitizeProps from governance module

/**
 * Collect URL attribution parameters
 */
function collectAttribution(): Record<string, string> {
  if (typeof window === 'undefined') return {};

  const urlParams = new URLSearchParams(window.location.search);
  const attribution: Record<string, string> = {};

  const params = [
    'src',
    'host',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
  ];

  for (const param of params) {
    const value = urlParams.get(param);
    if (value) {
      attribution[param] = value;
    }
  }

  return attribution;
}

/**
 * Initialize analytics (PostHog only; Clarity/SalesIQ load separately)
 * Respects user consent - only initializes if consent granted
 */
export function initAnalytics(): void {
  if (analyticsInitialized || typeof window === 'undefined') return;

  analyticsInitialized = true;

  // P20250925-147: Check master feature flag first
  if (!ANALYTICS_FLAGS.FEATURE_ANALYTICS_ENABLED) {
    if (import.meta.env.DEV) {
      console.info(
        '[Analytics] Disabled via VITE_FEATURE_ANALYTICS_ENABLED=false (master kill switch)',
      );
    }
    return;
  }

  if (!ANALYTICS_FLAGS.ANALYTICS_ENABLED) {
    if (import.meta.env.DEV) {
      console.info('[Analytics] Disabled via VITE_ANALYTICS_ENABLED=false');
    }
    return;
  }

  // Check consent before initializing
  const consent = getConsent();
  const hasAnalyticsConsent = consent?.analytics === true;

  if (ANALYTICS_FLAGS.POSTHOG_ENABLED && hasAnalyticsConsent) {
    const ready = initPostHog();
    if (ready && import.meta.env.DEV) {
      console.info('[Analytics] PostHog initialized');
    }
  } else if (import.meta.env.DEV) {
    if (!ANALYTICS_FLAGS.POSTHOG_ENABLED) {
      console.info('[Analytics] PostHog disabled: no VITE_POSTHOG_KEY');
    } else {
      console.info('[Analytics] PostHog blocked: no consent');
    }
  }

  // Listen for consent changes
  onConsentChange((newConsent) => {
    if (newConsent.analytics && ANALYTICS_FLAGS.POSTHOG_ENABLED && !isPostHogReady()) {
      // User accepted - initialize PostHog now
      initPostHog();
      if (import.meta.env.DEV) {
        console.info('[Analytics] PostHog initialized after consent');
      }
    } else if (!newConsent.analytics && isPostHogReady()) {
      // User revoked - opt out
      if ((window as any).posthog) {
        (window as any).posthog.opt_out_capturing();
        if (import.meta.env.DEV) {
          console.info('[Analytics] PostHog opt-out');
        }
      }
    }
  });
}

/**
 * Main event tracking function
 *
 * Automatically sanitizes PII, adds session_id and attribution.
 * Respects user consent - no-ops if analytics rejected.
 */
export function track(name: string, props: Record<string, any> = {}): void {
  // P20250925-147: Check master feature flag
  if (!ANALYTICS_FLAGS.FEATURE_ANALYTICS_ENABLED || !ANALYTICS_FLAGS.ANALYTICS_ENABLED) return;

  // Check consent (except for consent-related events)
  const isConsentEvent = name.startsWith('consent_');
  if (!isConsentEvent) {
    const consent = getConsent();
    if (!consent || !consent.analytics) {
      // No consent or analytics disabled
      if (import.meta.env.DEV) {
        console.log(`[Analytics] ${name} blocked (no consent)`);
      }
      return;
    }
  }

  // Add context first
  const sessionId = getSessionId();
  const attribution = collectAttribution();

  const mergedProps = {
    ...props,
    session_id: sessionId,
    ...attribution,
  };

  // Sanitize PII (governance module)
  const sanitizedProps = sanitizeProps(mergedProps);

  // Send to PostHog
  if (ANALYTICS_FLAGS.POSTHOG_ENABLED && isPostHogReady()) {
    posthogCapture(name, sanitizedProps);
  }

  // Dev logging
  if (import.meta.env.DEV) {
    console.log(`[Analytics] ${name}`, sanitizedProps);
  }
}

/**
 * Legacy fireEvent alias (for backward compat)
 */
export const fireEvent = track;

/**
 * Get recent events (stub for admin cockpit)
 */
export function getRecentEvents(): Array<{ name: string; timestamp: number; props?: any }> {
  // Stub: PostHog doesn't expose client-side event buffer
  // In production, query PostHog API server-side or use PostHog session recordings
  return [];
}

/**
 * Export flags for admin UI
 */
export function getAnalyticsFlags(): typeof ANALYTICS_FLAGS {
  return { ...ANALYTICS_FLAGS };
}

// Initialize on module load (client-side only)
if (typeof window !== 'undefined') {
  // Use idle callback for better performance
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => initAnalytics());
  } else {
    setTimeout(() => initAnalytics(), 0);
  }
}
